import Home, * as home from 'pages/Home';
import ProductManagement, * as productManagement from 'pages/ProductManagement';
import ProductsUpload from 'pages/ProductManagement/subPages/ProductsUpload';
import ProductTitlesManagement, * as productTitlesManagement from 'pages/ProductTitles';
import TitleSplitAIGenerate from 'pages/ProductTitles/subPages/TitleSplitAIGenerate';
import KeywordsManagement, * as keywordsManagement from 'pages/Keywords';
import UserManagement, * as userManagement from 'pages/UserManagement';
import UserEdit from 'pages/UserManagement/components/Molecules/MUserEdit/UserEdit';
import UsersUpload from 'pages/UserManagement/components/SubPages/UserUpload';
import ImageManagment, * as imageManagement from 'pages/ImageManagement';
import ImageUpload from 'pages/ImageManagement/components/organisms/ImagesUpload/ImagesUpload';
import History, * as history from 'pages/History';
import Preferences, * as preferences from 'pages/Preferences';
import PDP from 'pages/pdp';
import PDPUpload from 'pages/pdp/subPages/upload';
import ListManagement, * as listManagement from 'pages/ListManagement';
import Sectorisation, * as sectorisation from 'pages/Sectorisation';
import ClusterManagement, * as clusterManagement from 'pages/ClusterManagement';
import SalesData, * as salesData from 'pages/SalesData';
import MListDataViewer from 'pages/ListManagement/components/Molecules/MListDataViewer';
import UploadRouter from 'router/UploadRouter';
import { guarded } from 'router/GuardedRoute';
import ImageDetail from 'pages/ImageManagement/components/organisms/DetailViewer/ImageDetail';
import SectoUpload from 'pages/Sectorisation/SectoUpload';
import SalesDataUpload from 'pages/SalesData/subPages/SalesDataUpload';

import icons from 'shared/icons';
import { RouteDec } from 'types';
import { useGetAuthorizedTabsSelector } from 'core/redux/selectors/authSelectors';
import UnauthorizedAccess from 'pages/UnauthorizedAccess';
import { subRoutes, TSubRoute } from './tabsMapper';

import App from '../App';

const ProductsIcon = icons.productBis;
const KeywordsIcon = icons.keywords;
const UsersIcon = icons.user;
const HomeIcon = icons.home;
const ImagesIcon = icons.image;
const HistoryIcon = icons.history;
const ListManagmentIcon = icons.listManagment;
const SectorisationIcon = icons.sectorisation;
const PreferencesIcon = icons.preferences;
const SalesDataIcon = icons.salesData;

const PDPRoute = () => {
    const { authorizedTabs } = useGetAuthorizedTabsSelector();
    return authorizedTabs.includes('cdm_pdp') ? <PDP /> : <UnauthorizedAccess />;
};

const PDPUploadRoute = () => {
    const { authorizedTabs } = useGetAuthorizedTabsSelector();
    return authorizedTabs.includes('cdm_pdp') ? <PDPUpload /> : <UnauthorizedAccess />;
};

const routes: RouteDec = {
    root: {
        element: App,
        path: '/',
        children: [
            {
                [home.ID]: {
                    element: Home,
                    path: home.PATH,
                    name: home.NAME,
                    icon: (props) => <HomeIcon {...props} />,
                },
                [imageManagement.ID]: {
                    element: guarded('cdm_picturesmanager', ImageManagment),
                    path: imageManagement.PATH,
                    name: imageManagement.NAME,
                    icon: (props) => <ImagesIcon {...props} />,
                    children: [
                        {
                            cdm_image_upload: {
                                element: guarded('cdm_picturesmanager', ImageUpload),
                                path: 'upload',
                            },
                        },
                        {
                            cdm_image_detail: {
                                element: guarded('cdm_picturesmanager', ImageDetail),
                                path: 'detail',
                            },
                        },
                    ],
                },
                [productManagement.ID]: {
                    element: guarded('cdm_clientsproduct', ProductManagement),
                    path: productManagement.PATH,
                    name: productManagement.NAME,
                    icon: (props) => <ProductsIcon {...props} />,
                    children: [
                        {
                            cdm_clientsproduct_upload: {
                                element: guarded('cdm_clientsproduct', ProductsUpload),
                                path: 'upload',
                            },
                            cdm_pdp_upload: {
                                element: PDPUploadRoute,
                                path: 'pdp/upload',
                            },
                            cdm_pdp_tab: {
                                element: PDPRoute,
                                path: 'pdp',
                            },
                            cdm_producttitlesplit_tab: {
                                element: guarded('cdm_producttitlesplit', ProductTitlesManagement),
                                path: ':page',
                            },
                            cdm_producttitlesplit_upload: {
                                element: guarded('cdm_producttitlesplit', UploadRouter),
                                path: ':page/upload',
                            },
                            cdm_producttitlesplit_ai_generate: {
                                element: guarded('cdm_producttitlesplit', TitleSplitAIGenerate),
                                path: ':page/ai-generate',
                            },
                        },
                    ],
                },
                [keywordsManagement.ID]: {
                    element: guarded('cdm_keywords', KeywordsManagement),
                    path: keywordsManagement.PATH,
                    name: keywordsManagement.NAME,
                    icon: (props) => <KeywordsIcon {...props} />,
                    children: [
                        {
                            cdm_keywords_upload: {
                                element: guarded('cdm_keywords', UploadRouter),
                                path: ':page/upload/',
                            },
                        },
                        {
                            cdm_keywords_tab: {
                                element: guarded('cdm_keywords', KeywordsManagement),
                                path: ':page',
                            },
                        },
                    ],
                },
                [userManagement.ID]: {
                    element: guarded('cdm_usermanager', UserManagement),
                    path: userManagement.PATH,
                    name: userManagement.NAME,
                    icon: (props) => <UsersIcon {...props} />,
                    children: [
                        {
                            cdm_usermanager_add: {
                                element: guarded('cdm_usermanager', UserEdit),
                                path: ':page/add',
                            },
                            cdm_usermanager_edit: {
                                element: guarded('cdm_usermanager', UserEdit),
                                path: ':page/edit',
                            },
                            cdm_usermanager_upload: {
                                element: guarded('cdm_usermanager', UsersUpload),
                                path: ':page/upload',
                            },
                            cdm_usermanager_tab: {
                                element: guarded('cdm_usermanager', UserManagement),
                                path: ':page',
                            },
                        },
                    ],
                },
                [listManagement.ID]: {
                    element: guarded('cdm_listmanager', ListManagement),
                    path: listManagement.PATH,
                    name: listManagement.NAME,
                    icon: (props) => <ListManagmentIcon {...props} />,
                    children: [
                        {
                            cdm_listmanager_list: {
                                element: guarded('cdm_listmanager', MListDataViewer),
                                path: ':page/list',
                            },
                            cdm_listmanager_tab: {
                                element: guarded('cdm_listmanager', ListManagement),
                                path: ':page',
                            },
                        },
                    ],
                },
                [sectorisation.ID]: {
                    element: guarded('cdm_sectorisation', Sectorisation),
                    path: sectorisation.PATH,
                    name: sectorisation.NAME,
                    icon: (props) => <SectorisationIcon {...props} />,
                    children: [
                        {
                            cdm_sectomanager_upload: {
                                element: guarded('cdm_sectorisation', SectoUpload),
                                path: ':page/upload',
                            },
                            cdm_sectomanager_tab: {
                                element: guarded('cdm_sectorisation', Sectorisation),
                                path: ':page',
                            },
                        },
                    ],
                },
                [clusterManagement.ID]: {
                    element: guarded('cdm_cluster_management', ClusterManagement),
                    path: clusterManagement.PATH,
                    name: clusterManagement.NAME,
                    icon: (props) => <SectorisationIcon {...props} />,
                    children: [
                        {
                            cdm_cluster_management_pages: {
                                element: guarded('cdm_cluster_management', ClusterManagement),
                                path: ':page',
                            },
                            cdm_cluster_management_upload: {
                                element: guarded('cdm_cluster_management', UploadRouter),
                                path: ':page/upload',
                            },
                        },
                    ],
                },

                [salesData.ID]: {
                    element: guarded('cdm_sales_data', SalesData),
                    path: salesData.PATH,
                    name: salesData.NAME,

                    icon: (props) => <SalesDataIcon {...props} />,
                    children: [
                        {
                            cdm_sales_data_upload: {
                                element: guarded('cdm_sales_data', SalesDataUpload),
                                path: 'upload',
                            },
                            cdm_sales_data_tab: {
                                element: guarded('cdm_sales_data', SalesData),
                                path: ':page',
                            },
                        },
                    ],
                },
                [history.ID]: {
                    element: guarded('cdm_historypage', History),
                    path: history.PATH,
                    name: history.NAME,
                    icon: (props) => <HistoryIcon {...props} />,
                },
                [preferences.ID]: {
                    element: guarded('cdm_preferences', Preferences),
                    path: preferences.PATH,
                    name: preferences.NAME,
                    children: [
                        {
                            cdm_preferences_tab: {
                                element: guarded('cdm_preferences', Preferences),
                                path: ':page',
                            },
                            cdm_preferences_upload: {
                                element: guarded('cdm_preferences', UploadRouter),
                                path: ':page/upload',
                            },
                        },
                    ],
                    icon: (props) => <PreferencesIcon {...props} />,
                },
            },
        ],
    },
};

export const sideBarRoutes: {
    id: string;
    path: string;
    order: number;
    name: string;
    childs?: TSubRoute[];
}[] = [
    { id: home.ID, path: home.PATH, name: home.NAME, order: 1 },
    { id: imageManagement.ID, path: imageManagement.PATH, name: imageManagement.NAME, order: 2 },
    { id: keywordsManagement.ID, path: keywordsManagement.PATH, name: keywordsManagement.NAME, order: 3 },
    { id: productManagement.ID, path: productManagement.PATH, name: productManagement.NAME, order: 4 },
    { id: userManagement.ID, path: userManagement.PATH, name: userManagement.NAME, order: 5 },
    { id: listManagement.ID, path: listManagement.PATH, name: listManagement.NAME, order: 6 },
    { id: sectorisation.ID, path: sectorisation.PATH, name: sectorisation.NAME, order: 7 },
    { id: clusterManagement.ID, path: clusterManagement.PATH, name: clusterManagement.NAME, order: 8 },
    { id: salesData.ID, path: salesData.PATH, name: salesData.NAME, order: 9 },
    { id: history.ID, path: history.PATH, name: history.NAME, order: 10 },
    { id: preferences.ID, path: preferences.PATH, name: preferences.NAME, order: 11 },
].map((route) => ({ ...route, childs: subRoutes[route.path] }));

export default routes;
